/**
 * Cache-row-first playbook resolution for the Flow Designer viewer.
 *
 * The executing agent reads a node's playbook from its cache row's
 * `playbook_json` (falling back to the node's configJson playbook when the row
 * has none). This mirrors that contract on the frontend: when a node's cache row
 * carries an authored playbook, swap it in for the node's configJson steps so the
 * viewer renders what the agent actually reads. Per-step screenshots are grafted
 * from the row's `stepScreenshots` map (the cache-row playbook strips
 * `screenshotS3Key`, which lives in `playbook_meta.stepScreenshots`).
 *
 * Resolving once — and feeding the resolved nodes through the whole viewer —
 * keeps the rendered step list and the step↔compiledEntries matching reading the
 * SAME playbook. A half-cutover (cache-row steps matched against
 * configJson-derived entries) is the drift hazard this avoids.
 *
 * @module FlowDesigner/utils/resolveCachePlaybookNodes
 */

import type { PlaybookStep } from '@chatsdet/types';
import type { EffectivePlaybookData } from '../types/playbook-cache';

type NodeLike = { id: string; data?: Record<string, unknown> | null };

export function resolveCachePlaybookNodes<T extends NodeLike>(
  nodes: T[],
  effectiveByNodeId: Map<string, EffectivePlaybookData>
): T[] {
  return nodes.map((node) => {
    const playbook = effectiveByNodeId.get(node.id);
    if (!playbook) return node;

    const shots = playbook.activeCache?.stepScreenshots ?? {};
    const merged: PlaybookStep[] = playbook.steps.map((step) => ({
      ...step,
      screenshotS3Key: shots[String(step.order)] ?? step.screenshotS3Key,
    }));
    return { ...node, data: { ...(node.data ?? {}), playbookSteps: merged } };
  });
}
